import { ADELIS_URL, LOGO_URL } from "@/lib/rn3";
import { SocialIcons } from "@/components/Layout";
import { Link } from "react-router-dom";
import { Heart, Compass, Sparkles, Globe2, ShieldCheck, ArrowRight, BookOpen, Quote } from "lucide-react";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";

import { useT } from "@/lib/i18n";
import SEO from "@/components/SEO";

const About = () => {
  const t = useT();
  const cards = [
    { icon: Heart, key: "mission" },
    { icon: Compass, key: "story" },
    { icon: Sparkles, key: "why" },
  ];
  return (
    <div className="bg-white">
      <SEO
        title="About Adelis Roman — Roman Numerals III | Bilingual Bookkeeper in Jacksonville, FL"
        description="Meet Adelis Roman, owner of Roman Numerals III — a bilingual (English/Spanish) bookkeeper, tax preparer, and notary serving Jacksonville and the State of Florida."
        path="/about"
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "About", path: "/about" }]}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Person",
          "@id": "https://romannumeralsiii.com/about#adelis",
          "name": "Adelis Roman",
          "jobTitle": "Owner",
          "worksFor": { "@id": "https://romannumeralsiii.com/#organization" },
          "knowsLanguage": ["en", "es"],
          "knowsAbout": ["Bookkeeping", "Tax Preparation", "Notary Public", "ITIN", "Sales Tax", "Spanish Translation"],
          "address": { "@type": "PostalAddress", "addressLocality": "Jacksonville", "addressRegion": "FL", "addressCountry": "US" }
        }}
      />
      <section className="bg-[#FDFBF7]" data-testid="about-hero">
        <div className="rn3-container py-20 md:py-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 flex justify-center lg:justify-start">
            <div className="relative w-72 h-72 sm:w-96 sm:h-96 flex items-center justify-center bg-white border border-[#E5E5E5] shadow-xl">
              <div className="absolute inset-3 border border-[#C4A252]" aria-hidden="true" />
              <img
                src={LOGO_URL}
                alt="Roman Numerals III"
                className="w-3/4 h-3/4 object-contain relative"
                data-testid="about-logo-img"
              />
            </div>
          </div>
          <div className="lg:col-span-7 rn3-fade-up">
            <div className="rn3-eyebrow">{t("about.eyebrow")} · {t("about.meet")}</div>
            <h1 className="font-serif-display text-5xl lg:text-6xl mt-4 leading-tight text-[#0A0A0A]">
              {t("about.h1_a")} <span className="italic text-[#8B1C22]">{t("about.h1_b")}</span>
            </h1>
            <div className="border-t border-[#C4A252] w-12 my-6" />
            <p className="text-[#404040] leading-relaxed">
              {t("about.intro_1")}
            </p>
            <p className="text-[#404040] leading-relaxed mt-4">
              {t("about.intro_2_a")} <b>Adelis</b>{t("about.intro_2_b")}
            </p>
          </div>
        </div>
      </section>

      {/* JOURNEY — Expandable Dropdown */}
      <section className="rn3-section bg-white" data-testid="about-journey-section">
        <div className="rn3-container max-w-5xl">
          <div className="text-center">
            <div className="rn3-eyebrow">{t("about.journey.eyebrow")}</div>
            <h2 className="font-serif-display text-4xl md:text-5xl mt-3">
              {t("about.journey.h2_a")} <span className="italic text-[#8B1C22]">{t("about.journey.h2_b")}</span>
            </h2>
            <div className="border-t border-[#C4A252] w-12 mx-auto my-6" />
            <p className="text-[#404040] max-w-2xl mx-auto">
              {t("about.journey.sub")}
            </p>
          </div>

          <div className="mt-10 rn3-card p-0 overflow-hidden">
            <Accordion type="single" collapsible defaultValue="journey" data-testid="journey-accordion">
              <AccordionItem value="journey" className="border-b-0">
                <AccordionTrigger
                  className="px-6 md:px-8 py-5 hover:no-underline text-left"
                  data-testid="journey-trigger"
                >
                  <div className="flex items-center gap-3">
                    <BookOpen className="w-5 h-5 text-[#8B1C22]" />
                    <span className="font-serif-display text-xl md:text-2xl text-[#0A0A0A]">
                      {t("about.journey.title")}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 md:px-8 pb-10">
                  <div className="border-t border-[#E5E5E5] pt-8 grid md:grid-cols-12 gap-8" data-testid="journey-content">
                    <div className="md:col-span-5">
                      <img
                        src={ADELIS_URL}
                        alt="Adelis — Founder of Roman Numerals III"
                        className="w-full max-w-sm mx-auto md:mx-0 aspect-square object-cover rounded-full border-[5px] border-[#C4A252] shadow-lg"
                        data-testid="journey-adelis-img"
                      />
                      <div className="mt-6 text-center md:text-left">
                        <div className="font-serif-display text-2xl text-[#0A0A0A]">Adelis Roman</div>
                        <div className="text-xs uppercase tracking-[0.22em] text-[#737373] mt-1">{t("about.journey.founder")}</div>
                      </div>
                    </div>

                    <div className="md:col-span-7 text-[#404040] leading-relaxed text-base space-y-5">
                      <div className="flex">
                        <Quote className="w-6 h-6 text-[#C4A252] shrink-0 mr-3" />
                        <p className="italic text-[#0A0A0A]">
                          &ldquo;{t("about.journey.quote_1")}&rdquo;
                        </p>
                      </div>

                      <p>{t("about.journey.p1")}</p>
                      <p>{t("about.journey.p2")}</p>
                      <p>{t("about.journey.p3")}</p>
                      <p>{t("about.journey.p3b")}</p>

                      <div className="border-l-2 border-[#C4A252] pl-5 italic text-[#0A0A0A]">
                        &ldquo;{t("about.journey.quote_2")}&rdquo;
                      </div>

                      <p>
                        {t("about.journey.p4_a")} <b className="text-[#0A0A0A]">{t("about.journey.p4_b")}</b> {t("about.journey.p4_c")} <b className="text-[#8B1C22]">{t("about.journey.p4_d")}</b>
                      </p>

                      <p>{t("about.journey.p5")}</p>

                      <div className="pt-3 flex flex-wrap gap-3">
                        <Link to="/appointments" className="rn3-btn-primary" data-testid="journey-cta-schedule">
                          {t("about.journey.cta_schedule")}
                        </Link>
                        <Link to="/services" className="rn3-btn-gold" data-testid="journey-cta-services">
                          {t("about.journey.cta_services")} <ArrowRight className="w-4 h-4" />
                        </Link>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      <section className="rn3-section bg-[#FDFBF7]">
        <div className="rn3-container grid md:grid-cols-3 gap-6">
          {cards.map((b) => (
            <div key={b.key} className="rn3-card bg-white" data-testid={`about-card-${b.key}`}>
              <b.icon className="w-7 h-7 text-[#8B1C22]" />
              <h3 className="font-serif-display text-2xl mt-4 text-[#0A0A0A]">{t(`about.cards.${b.key}_title`)}</h3>
              <div className="border-t border-[#C4A252] w-10 mt-3 mb-4" />
              <p className="text-sm text-[#404040] leading-relaxed">{t(`about.cards.${b.key}_body`)}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#F3EFE9]">
        <div className="rn3-container py-16 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="rn3-eyebrow flex items-center gap-2"><Globe2 className="w-4 h-4" /> {t("about.bilingual.eyebrow")}</div>
            <h2 className="font-serif-display text-4xl mt-3 text-[#0A0A0A]">
              {t("about.bilingual.h2_a")} <span className="italic text-[#8B1C22]">{t("about.bilingual.h2_b")}</span>
            </h2>
            <p className="mt-4 text-[#404040] leading-relaxed max-w-md">
              {t("about.bilingual.body")}
            </p>
          </div>
          <div className="rn3-card bg-white">
            <ShieldCheck className="w-7 h-7 text-[#C4A252]" />
            <h3 className="font-serif-display text-2xl mt-4">{t("about.platform.title")}</h3>
            <div className="border-t border-[#C4A252] w-10 mt-3 mb-4" />
            <p className="text-sm text-[#404040] leading-relaxed">
              {t("about.platform.body")}
            </p>
            <Link to="/services" className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-[#8B1C22]" data-testid="about-services-link">
              {t("about.platform.link")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="rn3-section">
        <div className="rn3-container flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <div className="font-serif-display text-3xl">{t("about.follow.title")}</div>
            <p className="text-sm text-[#737373] mt-2">{t("about.follow.sub")}</p>
          </div>
          <SocialIcons />
        </div>
      </section>
    </div>
  );
};

export default About;
